/**
 * Command for moving text location in MLeader entities.
 *
 * Unlike TransformEntityCommand which moves the entire entity,
 * this command only moves the text location while keeping arrow tips fixed.
 */

import type { ICommand } from "./ICommand";
import type { DRDrawingViewer } from "../DRDrawingViewer";
import { deleteObj } from "../core/delete";
import { findEntityByHandle, openAs } from "../core/oda";
import { OpenMode } from "../core/constants";
import { recordGraphicsModified } from "../entities";
import type { Point3D } from "../transform/types";
import { oplogEmitter, type OplogEmit } from "../meta/oplog";

export interface MoveTextParams {
  /** Handle of the MLeader entity */
  entityHandle: string;
  /** Original text location */
  fromLocation: Point3D;
  /** New text location */
  toLocation: Point3D;
}

/**
 * Command to move text location in MLeader.
 */
export class MoveMLeaderTextCommand implements ICommand {
  private viewer: DRDrawingViewer;
  private params: MoveTextParams;
  private executed = false;
  private oplogEvents: OplogEmit[] = [];

  constructor(viewer: DRDrawingViewer, params: MoveTextParams) {
    this.viewer = viewer;
    this.params = params;
  }

  /**
   * Mark as executed without actually applying the transform.
   * Used when the handle has already applied the transform during drag.
   */
  markAsExecuted(): void {
    this.executed = true;
  }

  /**
   * L'oplog doit être émis même si `markAsExecuted()` a déjà été appelé (le guard
   * `executed` ne doit pas le court-circuiter) — CommandHistory n'appelle execute()
   * qu'une seule fois de toute façon.
   */
  execute(): void {
    this.oplogEvents = oplogEmitter.emitModified(this.viewer, [
      { handle: this.params.entityHandle, changeKind: "moved" },
    ]);
    if (this.executed) return;
    this.setTextLocation(this.params.toLocation);
    this.executed = true;
  }

  undo(): void {
    if (!this.executed) return;
    this.setTextLocation(this.params.fromLocation);
    this.executed = false;
    this.viewer.update();
    oplogEmitter.revert(this.oplogEvents);
  }

  redo(): void {
    if (this.executed) return;
    this.setTextLocation(this.params.toLocation);
    this.executed = true;
    this.viewer.update();
    oplogEmitter.reemit(this.oplogEvents);
  }

  dispose(): void {
    // Nothing to clean up
  }

  private setTextLocation(location: Point3D): void {
    const db = this.viewer.database;
    if (!db) return;

    const module = this.viewer.module;

    // Use resolveCurrentHandle in case the MLeader was recreated
    const currentHandle = this.params.entityHandle;
    const entityId = findEntityByHandle(module, db, currentHandle);
    if (!entityId) return;

    const mleader = openAs(entityId, module.OdDbMLeader, OpenMode.kForWrite);

    const point = new module.OdGePoint3d(location.x, location.y, location.z);
    mleader.setTextLocation(point);
    deleteObj(point);

    recordGraphicsModified(module, entityId);
    deleteObj(entityId);
    deleteObj(mleader);
  }
}
